// 注册页 —— 邮箱 / 密码 / 公司名 / Google 一键注册。

import { useState } from 'react';
import { register, login, oauthStartUrl, ApiError } from '../lib/api';
import { writeSession } from '../lib/storage';
import { navigate } from '../lib/route';

export function Register(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (password.length < 12) {
      setErr('密码至少 12 位');
      return;
    }
    if (company.trim().length === 0) {
      setErr('请填写公司或团队名');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await register({ email, password, company: company.trim() });
      // 注册成功后顺手登录
      try {
        const res = await login(email, password);
        if ('jwt' in res) {
          writeSession({ token: res.jwt, email, role: 'owner' });
          navigate('sites');
          return;
        }
      } catch {
        /* 注册成功但登录失败 → 仍然显示成功提示 */
      }
      setDone(true);
    } catch (ex) {
      if (ex instanceof ApiError) {
        if (ex.code === 'EMAIL_EXISTS') setErr('该邮箱已注册');
        else setErr(ex.message);
      } else {
        setErr(ex instanceof Error ? ex.message : '注册失败');
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={brandStyle}>
            <span style={logoBoxStyle}>🛡️</span>
            <span>Captcha SaaS</span>
          </div>
          <h1 style={titleStyle}>注册成功</h1>
          <p style={subtitleStyle}>验证邮件已发送至 {email}，请前往邮箱完成验证。</p>
          <a
            href="#/login"
            style={{ ...primaryBtn, display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 24 }}
            onClick={(e) => {
              e.preventDefault();
              navigate('login');
            }}
          >
            前往登录
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={brandStyle}>
          <span style={logoBoxStyle}>🛡️</span>
          <span>Captcha SaaS</span>
        </div>
        <h1 style={titleStyle}>创建账号</h1>
        <p style={subtitleStyle}>免费额度 10 万次/月，无需信用卡</p>

        <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
          <Field label="公司或团队名">
            <input
              required
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="My Company"
              style={inputStyle}
            />
          </Field>
          <Field label="邮箱">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={inputStyle}
            />
          </Field>
          <Field label="密码">
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="至少 12 位"
              style={inputStyle}
            />
            <div style={hintStyle}>密码长度需 ≥ 12 位</div>
          </Field>
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy ? '创建中…' : '创建账号'}
          </button>
          {err && <div style={errorBoxStyle}>{err}</div>}
        </form>

        <div style={dividerStyle}>
          <span style={dividerLineStyle} />
          <span style={dividerTextStyle}>或</span>
          <span style={dividerLineStyle} />
        </div>

        <a href={oauthStartUrl('google')} style={googleBtn}>
          <GoogleIcon />
          使用 Google 注册
        </a>

        <div style={footerLinksStyle}>
          已有账号？
          <a
            href="#/login"
            style={linkStyle}
            onClick={(e) => {
              e.preventDefault();
              navigate('login');
            }}
          >
            前往登录
          </a>
        </div>
      </div>
    </div>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={labelStyle}>{props.label}</div>
      {props.children}
    </label>
  );
}

function GoogleIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.93 8.93 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

// ---- styles ----
const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
};
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 14,
  padding: 32,
  boxShadow: '0 24px 50px -28px rgba(15,23,42,.18)',
};
const brandStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 24,
  color: '#0f172a',
};
const logoBoxStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  display: 'inline-grid',
  placeItems: 'center',
  fontSize: 16,
};
const titleStyle: React.CSSProperties = { fontSize: 24, margin: 0, color: '#0f172a' };
const subtitleStyle: React.CSSProperties = { fontSize: 14, color: '#64748b', margin: '8px 0 0' };
const labelStyle: React.CSSProperties = { fontSize: 12, color: '#475569', marginBottom: 6, fontWeight: 500 };
const hintStyle: React.CSSProperties = { fontSize: 11, color: '#94a3b8', marginTop: 4 };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
};
const primaryBtn: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  color: '#fff',
  border: 0,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  marginTop: 4,
};
const errorBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 10,
  fontSize: 13,
  color: '#991b1b',
  background: '#fee2e2',
  borderRadius: 8,
  border: '1px solid #fecaca',
};
const dividerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' };
const dividerLineStyle: React.CSSProperties = { flex: 1, height: 1, background: '#e2e8f0' };
const dividerTextStyle: React.CSSProperties = { fontSize: 12, color: '#94a3b8' };
const googleBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  padding: '11px 14px',
  background: '#fff',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
};
const footerLinksStyle: React.CSSProperties = { marginTop: 20, fontSize: 13, color: '#64748b', textAlign: 'center' };
const linkStyle: React.CSSProperties = { color: '#2563eb', textDecoration: 'none', marginLeft: 4, fontWeight: 500 };
